import type { Option } from "@tento-lona/sheets";
import type { Rational } from "@tento-core/rational";
import type { RowLocalId } from "@tento-lona/sheets";
import type { SheetRowTree } from "./sheet-tree.ts";
import type { SheetRow } from "./sheet-row.ts";

/** The parent id of a top-level row in a view-model tree. */
export const RootNode: unique symbol = Symbol("RootNode");
export type RootNode = typeof RootNode;

/** One row's place in its parent: its id, fractional position and parent. */
export class SheetRowEdge {
  readonly #tree: SheetRowTree;
  constructor(
    readonly id: RowLocalId,
    readonly position: Rational,
    readonly parentId: RowLocalId | RootNode,
    tree: SheetRowTree,
  ) {
    this.#tree = tree;
  }

  childrenIds(): RowLocalId[] {
    // Read from the rows-side canonical tree (via `tree.children`,
    // which consults `SheetRowTree.childrenIds` populated from
    // `Sheet.parentOf` / `childrenOf`). The legacy `tree.inner` only knows
    // network-shaped rows and misses synthesized virtual rows
    // (alias-template children, dynamicTail, activeLayout layout
    // rows) — using it here strands plugins that walk descendants
    // through this edge.
    return this.#tree.children(this.id).map((c) => c.id);
  }

  children(): SheetRow.Joined.Handle[] {
    return this.#tree.children(this.id);
  }

  parent(): Option<SheetRow.Joined.Handle> {
    if (typeof this.parentId === "symbol") return null;
    return this.#tree.get(this.parentId);
  }

  descendants(
    select: (row: SheetRow.Joined.Handle) => boolean,
    stop: Option<(row: SheetRow.Joined.Handle) => boolean>,
  ): SheetRow.Joined.Handle[] {
    const result: SheetRow.Joined.Handle[] = [];
    const walk = (ids: RowLocalId[]) => {
      for (const cid of ids) {
        const child = this.#tree.get(cid);
        if (!child) continue;
        if (stop?.(child)) continue;
        if (select(child)) result.push(child);
        walk(child.childrenIds());
      }
    };
    walk(this.childrenIds());
    return result;
  }
}
